import React from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Navigation } from "lucide-react";
import { NAV_ITEMS } from "../../data/nav";
import { CONTACT, whatsappUrl } from "../../lib/email";

const Footer = () => {
  const year = new Date().getFullYear();

  // Optimized WhatsApp pre-filled message
  const whatsappMessage = `Hello GlowLAWNS Team,
I would like to check availability and packages for Moonrise Valley.
Please assist me with details.`;

  return (
    <footer className="border-t border-white/10 bg-black/50" data-testid="footer">
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-4 py-14 md:grid-cols-12">
        {/* Brand Section */}
        <div className="md:col-span-5">
          <Link to="/" className="flex items-center gap-4 group">
            {/* Logo - Larger, clearer for footer */}
            <img
              src="/glowlawns-logo-footer@2x.png"
              alt="glowLAWNS"
              className="h-14 md:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
              style={{
                filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
              }}
            />
            <div className="leading-tight">
              <div className="text-base md:text-lg font-semibold tracking-[0.12em] uppercase text-white group-hover:text-[#C9A227] transition">
                glowLAWNS<span className="text-[#C9A227]">®</span>
              </div>
              <div className="text-[10px] md:text-xs text-white/50 tracking-wider mt-0.5">
                Forest Luxury Retreat
              </div>
            </div>
          </Link>

          <p className="mt-5 max-w-md text-sm leading-relaxed text-white/70">
            Forest-luxury stays crafted with restraint and respect. Expect quiet comfort, warm
            hospitality, and nature-led experiences—100% grounded in Kavilumpara, Kerala.
          </p>

          {/* CTA Buttons - Book Now + WhatsApp */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/contact"
              className="flex-1 rounded-xl bg-[#C9A227] px-6 py-3.5 text-center text-sm font-semibold text-[#111] shadow-[0_10px_40px_rgba(201,162,39,0.25)] hover:bg-[#d4b03a] hover:-translate-y-0.5 transition-all duration-300"
              data-testid="footer-book-now"
            >
              Book Now
            </Link>
            <a
              href={whatsappUrl(whatsappMessage)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-green-600/20 border border-green-500/30 text-green-400 text-sm font-medium hover:bg-green-600/30 transition-all duration-300"
              data-testid="footer-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              <span className="hidden sm:inline">Instant Booking</span>
              <span className="sm:hidden">WhatsApp</span>
            </a>
          </div>
        </div>

        {/* Navigation Links */}
        <div className="md:col-span-3">
          <div className="text-sm font-semibold text-white">Explore</div>
          <ul className="mt-4 space-y-2 text-sm text-white/70">
            {NAV_ITEMS.map((item) => (
              <li key={item.href}>
                <Link className="hover:text-white transition" to={item.href}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Contact Section */}
        <div className="md:col-span-4">
          <div className="text-sm font-semibold text-white">Contact Us</div>
          <div className="mt-4 space-y-4 text-sm text-white/70">
            {/* Address */}
            <div>{CONTACT.address}</div>
            
            {/* Get Directions Button */}
            <a 
              href="https://www.google.com/maps/dir/?api=1&destination=PR2J%2BJV+Kavilumpara,+Kerala"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white/90 text-xs font-medium hover:bg-white/15 hover:border-[#C9A227]/50 transition-all duration-300"
              data-testid="footer-directions-btn"
            >
              <Navigation className="w-3.5 h-3.5" />
              Get Directions
            </a>
            
            {/* Booking Helpline - Both numbers */}
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider mb-1">Booking Helpline</div>
              <a
                className="block text-white hover:text-[#C9A227] transition font-medium"
                href={`tel:${CONTACT.primaryPhone.replace(/-/g, "")}`}
              >
                {CONTACT.primaryPhone}
              </a>
              <a
                className="block text-white hover:text-[#C9A227] transition font-medium mt-1"
                href={`tel:${CONTACT.supportPhone.replace(/-/g, "")}`}
              >
                {CONTACT.supportPhone}
              </a>
            </div>
            
            {/* Booking Email */}
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider mb-1">For Bookings</div>
              <a
                className="block hover:text-white transition"
                href={`mailto:${CONTACT.emails.booking}`}
              >
                {CONTACT.emails.booking}
              </a>
            </div>
            
            {/* Official & Business Emails */}
            <div>
              <div className="text-white/60 text-xs uppercase tracking-wider mb-1">Official & Business Inquiries</div>
              <a
                className="block hover:text-white transition"
                href={`mailto:${CONTACT.emails.operations}`}
              >
                {CONTACT.emails.operations}
              </a>
              <a
                className="block hover:text-white transition mt-1"
                href={`mailto:${CONTACT.emails.admin}`}
              >
                {CONTACT.emails.admin}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright Bar */}
      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-6xl flex-col items-start justify-between gap-2 px-4 py-6 text-xs text-white/55 md:flex-row md:items-center">
          <div>© {year} glowLAWNS. All rights reserved.</div>
          <div className="flex items-center gap-4">
            <Link to="/policies" className="hover:text-white transition">Resort Policies</Link>
            <span className="text-white/30">|</span>
            <span>Nature-led experiences may vary with weather and season.</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
